// Shared cart store — persists to localStorage so a diner can browse Canvas
// or leave the tab and come back. Zero deps, works across all pages.
import { useEffect, useState, useCallback } from 'react';

const KEY = 'xin.food.cart.v1';
const OUTLET_KEY = 'xin.food.outlet.v1';

function read() {
  try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; }
}
function write(lines) {
  try { localStorage.setItem(KEY, JSON.stringify(lines)); } catch {}
  window.dispatchEvent(new CustomEvent('xin-cart-change'));
}

export function useFoodCart() {
  const [lines, setLines] = useState(read);
  const [outlet, setOutletState] = useState(() => localStorage.getItem(OUTLET_KEY) || 'koramangala');

  useEffect(() => {
    const onChange = () => setLines(read());
    window.addEventListener('xin-cart-change', onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener('xin-cart-change', onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);

  const setOutlet = useCallback((slug) => {
    try { localStorage.setItem(OUTLET_KEY, slug); } catch {}
    setOutletState(slug);
  }, []);

  const add = useCallback((line) => {
    // line: { item_slug, name, variant_label, unit_price }
    const cur = read();
    const key = `${line.item_slug}::${line.variant_label || ''}`;
    const found = cur.find((l) => `${l.item_slug}::${l.variant_label || ''}` === key);
    if (found) {
      found.qty = Math.min(99, (found.qty || 0) + 1);
    } else {
      cur.push({ ...line, qty: 1 });
    }
    write(cur);
  }, []);

  const remove = useCallback((item_slug, variant_label) => {
    const cur = read().filter((l) => !(l.item_slug === item_slug && (l.variant_label || '') === (variant_label || '')));
    write(cur);
  }, []);

  const setQty = useCallback((item_slug, variant_label, qty) => {
    const cur = read();
    const found = cur.find((l) => l.item_slug === item_slug && (l.variant_label || '') === (variant_label || ''));
    if (!found) return;
    if (qty <= 0) {
      write(cur.filter((l) => l !== found));
    } else {
      found.qty = Math.min(99, qty);
      write(cur);
    }
  }, []);

  const clear = useCallback(() => write([]), []);

  const totalQty = lines.reduce((s, l) => s + (l.qty || 0), 0);
  const subtotal = lines.reduce((s, l) => s + (l.qty || 0) * (l.unit_price || 0), 0);

  return { lines, add, remove, setQty, clear, totalQty, subtotal, outlet, setOutlet };
}
